import { useState, useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Search, ChevronDown, Accessibility, Wind, ArrowUpFromLine, BedDouble, Thermometer,
  Paintbrush, Package, Droplets,
} from 'lucide-react';

// Shared equipment-linking picker — reads GET /inventory/bom-equipment's flat `entries` list
// (real bom_equipment rows only) and lets a caller attach one to whatever it's linking equipment
// to (Learning Hub entries, Work Orders). Originally lived in LearningHub.tsx; extracted here so
// Tasks.tsx (Work Orders) can reuse it without duplicating the drill-down UI.

export interface BomEquipmentOption {
  id: number;
  type: string;
  displayName: string;
  brand: string | null;
  model: string | null;
}

export function equipmentLabel(o: { display_name?: string; displayName?: string; brand: string | null; model: string | null }): string {
  const base = o.display_name ?? o.displayName ?? '';
  return [base, o.brand, o.model].filter(Boolean).join(' — ');
}

const EQUIPMENT_TYPE_ICONS: Record<string, string> = {
  wheelchair: 'Accessibility', concentrator: 'Wind', lift: 'ArrowUpFromLine',
  bed: 'BedDouble', hvac: 'Thermometer', paint: 'Paintbrush',
  other: 'Package', shower_chair: 'Droplets', shower_chairs: 'Droplets',
};
const EQUIPMENT_ICON_COMPONENTS: Record<string, typeof Package> = {
  Accessibility, Wind, ArrowUpFromLine, BedDouble, Thermometer, Paintbrush, Package, Droplets,
};

export function getEquipmentTypeIcon(type: string) {
  const name = EQUIPMENT_TYPE_ICONS[type.toLowerCase()] ?? 'Package';
  const Comp = EQUIPMENT_ICON_COMPONENTS[name] ?? Package;
  return <Comp size={16} color="var(--text-secondary)" />;
}

interface EquipmentTypeGroup {
  type: string;
  displayName: string;
  typeOnlyEntry: BomEquipmentOption | null; // brand=null, model=null row, if one exists
  brands: {
    brand: string;
    brandOnlyEntry: BomEquipmentOption | null; // brand set, model=null row, if one exists
    models: BomEquipmentOption[]; // brand set, model set rows
  }[];
}

export function EquipmentLinkPicker({ entries, onSelect, disabled }: {
  entries: BomEquipmentOption[];
  onSelect: (id: number) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [drillType, setDrillType] = useState<string | null>(null);
  const [drillBrand, setDrillBrand] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ left: 0, width: 0 });

  const groups = useMemo<EquipmentTypeGroup[]>(() => {
    const byType = new Map<string, EquipmentTypeGroup>();
    for (const o of entries) {
      let g = byType.get(o.type);
      if (!g) { g = { type: o.type, displayName: o.displayName || o.type, typeOnlyEntry: null, brands: [] }; byType.set(o.type, g); }
      if (!o.brand) { g.typeOnlyEntry = o; continue; }
      let b = g.brands.find((x) => x.brand === o.brand);
      if (!b) { b = { brand: o.brand, brandOnlyEntry: null, models: [] }; g.brands.push(b); }
      if (!o.model) b.brandOnlyEntry = o;
      else b.models.push(o);
    }
    return Array.from(byType.values());
  }, [entries]);

  function close() {
    setOpen(false); setDrillType(null); setDrillBrand(null); setSearchQuery('');
  }

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))
      ) close();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function openDropdown() {
    if (disabled) return;
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const maxDropdownH = 322;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < maxDropdownH) {
        setDropdownPos({ bottom: window.innerHeight - rect.top + 2, left: rect.left, width: rect.width });
      } else {
        setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
      }
    }
    setOpen((prev) => !prev);
  }

  function selectEntry(id: number) {
    onSelect(id);
    close();
  }

  const filteredGroups = searchQuery
    ? groups.filter((g) => {
        const q = searchQuery.toLowerCase();
        return g.displayName.toLowerCase().includes(q) || g.brands.some((b) => b.brand.toLowerCase().includes(q));
      })
    : groups;

  const activeGroup = drillType ? groups.find((g) => g.type === drillType) : null;
  const activeBrand = activeGroup && drillBrand ? activeGroup.brands.find((b) => b.brand === drillBrand) : null;

  return (
    <div ref={containerRef}>
      <div
        onClick={openDropdown}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 10px', border: '1px solid rgba(255,255,255,0.13)',
          borderRadius: 6, background: 'var(--bg-elevated)', cursor: disabled ? 'default' : 'pointer',
          minHeight: 34, boxSizing: 'border-box', opacity: disabled ? 0.6 : 1,
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>Link equipment…</span>
        <ChevronDown size={14} style={{ opacity: 0.5, flexShrink: 0 }} />
      </div>
      {open && ReactDOM.createPortal(
        <div ref={dropdownRef} style={{
          position: 'fixed',
          ...(dropdownPos.top !== undefined ? { top: dropdownPos.top } : { bottom: dropdownPos.bottom }),
          left: dropdownPos.left, minWidth: Math.max(dropdownPos.width || 0, 420),
          zIndex: 10001, background: 'var(--bg-elevated)',
          border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8,
          color: 'var(--text-primary)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column', maxHeight: 320,
        }}>
          {/* Search bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <Search size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              autoFocus
              placeholder="Search all equipment…"
              value={searchQuery}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ fontSize: 12, background: 'transparent', border: 'none', outline: 'none', flex: 1, color: 'var(--text-primary)' }}
            />
          </div>

          {/* Two-column split */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', flex: 1, overflow: 'hidden', minHeight: 0 }}>
            {/* LEFT COLUMN — type / brand */}
            <div style={{ borderRight: '0.5px solid rgba(255,255,255,0.08)', overflowY: 'auto', maxHeight: 260 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '5px 10px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', position: 'sticky', top: 0, background: 'var(--bg-elevated)' }}>
                Type / Brand
              </div>
              {filteredGroups.map((g) => {
                const isExpanded = drillType === g.type;
                return (
                  <div key={g.type}>
                    <div
                      onMouseDown={(e) => { e.stopPropagation(); setDrillType(isExpanded ? null : g.type); setDrillBrand(null); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 12px', cursor: 'pointer', background: isExpanded ? 'var(--bg-hover)' : 'transparent' }}
                      onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                      onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ flexShrink: 0, display: 'flex' }}>{getEquipmentTypeIcon(g.type)}</span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.displayName}</span>
                      {g.brands.length > 0 && (
                        <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 99, background: 'rgba(255,255,255,0.08)', color: 'var(--text-muted)', flexShrink: 0 }}>{g.brands.length}</span>
                      )}
                    </div>
                    {isExpanded && (
                      <div>
                        {g.typeOnlyEntry && (
                          <div
                            onMouseDown={(e) => { e.stopPropagation(); selectEntry(g.typeOnlyEntry!.id); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px 7px 28px', cursor: 'pointer' }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                          >
                            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{g.displayName} (general)</span>
                          </div>
                        )}
                        {g.brands.map((b) => {
                          const isActiveBrand = drillBrand === b.brand && drillType === g.type;
                          return (
                            <div
                              key={b.brand}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                if (b.models.length > 0) setDrillBrand(isActiveBrand ? null : b.brand);
                                else if (b.brandOnlyEntry) selectEntry(b.brandOnlyEntry.id);
                              }}
                              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px 7px 28px', cursor: 'pointer', background: isActiveBrand ? 'rgba(59,130,246,0.08)' : 'transparent' }}
                              onMouseEnter={(e) => { if (!isActiveBrand) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                              onMouseLeave={(e) => { if (!isActiveBrand) e.currentTarget.style.background = 'transparent'; }}
                            >
                              <span style={{ fontSize: 13, color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.brand}</span>
                              {b.models.length > 0 && (
                                <>
                                  <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 99, background: 'rgba(255,255,255,0.08)', color: 'var(--text-muted)', flexShrink: 0 }}>{b.models.length}</span>
                                  <span style={{ fontSize: 13, color: 'var(--text-muted)', flexShrink: 0 }}>›</span>
                                </>
                              )}
                            </div>
                          );
                        })}
                        {g.brands.length === 0 && !g.typeOnlyEntry && (
                          <div style={{ padding: '5px 10px 5px 24px', fontSize: 11, color: 'var(--text-muted)' }}>No options</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredGroups.length === 0 && (
                <div style={{ padding: '12px 10px', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>No equipment found</div>
              )}
            </div>

            {/* RIGHT COLUMN — models */}
            <div style={{ overflowY: 'auto', maxHeight: 260 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '5px 10px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', position: 'sticky', top: 0, background: 'var(--bg-elevated)' }}>
                Model
              </div>
              {!activeBrand && (
                <div style={{ padding: '20px 10px', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                  Select a brand to see models
                </div>
              )}
              {activeBrand && (
                <>
                  {activeBrand.brandOnlyEntry && (
                    <div
                      onMouseDown={(e) => { e.stopPropagation(); selectEntry(activeBrand.brandOnlyEntry!.id); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{activeBrand.brand} (general)</span>
                    </div>
                  )}
                  {activeBrand.models.map((m) => (
                    <div
                      key={m.id}
                      onMouseDown={(e) => { e.stopPropagation(); selectEntry(m.id); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{m.model}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
